import React, { useState, useMemo } from 'react';
import * as Icons from 'lucide-react';
import { Check, Sparkles, Search, Plus, X, UserPlus } from 'lucide-react';
import { Button } from './Button';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import {
  useEnabledPersonas,
  usePersonas,
  useUpdatePersonaSetting,
} from '../helpers/usePersonasApi';
import {
  useSessionAiParticipants,
  useAddAiParticipant,
  useRemoveAiParticipant,
  useActivateAiParticipant,
} from '../helpers/useSessionAiParticipants';
import { toast } from 'sonner';
import styles from './PersonaSelector.module.css';
import { safeCap } from '../helpers/stringUtils';

interface PersonaSelectorProps {
  sessionId?: string;
  className?: string;
}

export const PersonaSelector: React.FC<PersonaSelectorProps> = ({ sessionId, className }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'my' | 'session'>('my');
  const [searchQuery, setSearchQuery] = useState('');
  const [addingPersonaId, setAddingPersonaId] = useState<string | null>(null);
  const [showAiCreate, setShowAiCreate] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const aiInputRef = React.useRef<HTMLTextAreaElement>(null);

  // My Personas hooks
  const { personas: enabledPersonas } = useEnabledPersonas({ enabled: open });
  const { data: allPersonasData, isLoading: isLoadingPersonas } = usePersonas({ enabled: open });
  const updateSetting = useUpdatePersonaSetting();

  // Session participants hooks
  const { data: participants = [], isLoading: isLoadingParticipants } = useSessionAiParticipants(
    sessionId || '',
  );
  const addParticipant = useAddAiParticipant();
  const removeParticipant = useRemoveAiParticipant();
  const activateParticipant = useActivateAiParticipant();

  const allPersonas = allPersonasData?.personas ?? [];
  const enabledCount = enabledPersonas.length;
  // Each participant is a unique individual — just count rows
  const sessionCount = participants.length;

  // Group participants by personaId for display
  const participantsByPersona = useMemo(() => {
    const map = new Map<string, typeof participants>();
    for (const p of participants) {
      const list = map.get(p.personaId) || [];
      list.push(p);
      map.set(p.personaId, list);
    }
    return map;
  }, [participants]);

  // Set of persona IDs that have at least one active participant
  const activePersonaIds = useMemo(
    () => new Set(participants.map((p) => p.personaId)),
    [participants],
  );

  // Filter personas based on search query
  const filteredPersonas = useMemo(() => {
    if (!searchQuery.trim()) return allPersonas;
    const query = searchQuery.toLowerCase();
    return allPersonas.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query),
    );
  }, [allPersonas, searchQuery]);

  // Sort personas: favorites (enabled) first, then alphabetically
  const sortedPersonas = useMemo(() => {
    return [...filteredPersonas].sort((a, b) => {
      if (a.isEnabled && !b.isEnabled) return -1;
      if (!a.isEnabled && b.isEnabled) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filteredPersonas]);

  // Sort for session tab: personas with active participants first
  const sortedForSession = useMemo(() => {
    return [...filteredPersonas].sort((a, b) => {
      const aActive = activePersonaIds.has(a.id);
      const bActive = activePersonaIds.has(b.id);
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filteredPersonas, activePersonaIds]);

  // Convert icon name to Lucide component
  const getIconComponent = (iconName: string) => {
    const componentName = iconName
      .split('-')
      .map((part, i) => (i === 0 ? part : safeCap(part)))
      .join('');
    const pascalName = safeCap(componentName);
    return (Icons as any)[pascalName] || Sparkles;
  };

  // My Personas handlers
  const handleTogglePersona = (personaId: string, currentlyEnabled: boolean) => {
    updateSetting.mutate({
      personaId,
      isEnabled: !currentlyEnabled,
    });
  };

  // Session handlers — each add creates a NEW individual participant
  const handleAddParticipant = async (personaId: string, personaName: string) => {
    if (!sessionId) return;
    setAddingPersonaId(personaId);
    try {
      await addParticipant.mutateAsync({ sessionId, personaId });
      // Auto-activate so they introduce themselves and start working immediately
      try {
        await activateParticipant.mutateAsync({ sessionId, personaId });
        toast.success(`New ${personaName} participant has joined and is ready to help!`);
      } catch (activateErr) {
        toast.success(`New ${personaName} participant has joined the conversation.`);
        console.error('Failed to auto-activate participant:', activateErr);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add participant');
    } finally {
      setAddingPersonaId(null);
    }
  };

  // Remove a specific individual participant by their row ID
  const handleRemoveParticipant = async (
    participantId: number,
    displayName: string | null,
    personaName: string,
  ) => {
    if (!sessionId) return;
    try {
      await removeParticipant.mutateAsync({ sessionId, participantId: String(participantId) });
      toast.success(`${displayName || personaName} has left the conversation.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove participant');
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim() || aiGenerating) return;
    setAiGenerating(true);
    try {
      const res = await fetch('/_api/personas/generate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: (await import('superjson')).default.stringify({ prompt: aiPrompt.trim() }),
      });
      const text = await res.text();
      let data: any;
      try { data = (await import('superjson')).default.parse(text); } catch { data = JSON.parse(text); }
      if (!res.ok) throw new Error(data?.error || 'Failed to generate persona');
      toast.success(`Created "${data.persona.name}"`);
      setAiPrompt('');
      setShowAiCreate(false);
      // Refetch personas
      updateSetting.mutate({ personaId: data.persona.id, isEnabled: true });
    } catch (e: any) {
      toast.error(e.message || 'Failed to generate persona');
    } finally {
      setAiGenerating(false);
    }
  };

  const isLoading = isLoadingPersonas || isLoadingParticipants;
  const totalActive = enabledCount + sessionCount;

  // Always show the button — even while loading or with no personas yet

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setSearchQuery('');
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant={totalActive > 0 ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.trigger}
            >
              <UserPlus size={14} />
              {totalActive > 0 && <span className={styles.count}>{totalActive}</span>}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {totalActive > 0
            ? `${enabledCount} persona${enabledCount !== 1 ? 's' : ''}, ${sessionCount} participant${sessionCount !== 1 ? 's' : ''} in session`
            : 'AI Personas'}
        </TooltipContent>
      </Tooltip>

      <PopoverContent
        className={styles.popover}
        align="end"
        side="top"
        sideOffset={8}
        removeBackgroundAndPadding
      >
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'my' | 'session')}>
          <div className={styles.header}>
            <TabsList className={styles.tabsList}>
              <TabsTrigger value="my" className={styles.tab}>
                AI Personas{' '}
                {enabledCount > 0 && <span className={styles.tabCount}>{enabledCount}</span>}
              </TabsTrigger>
              {sessionId && (
                <TabsTrigger value="session" className={styles.tab}>
                  AI Participants{' '}
                  {sessionCount > 0 && <span className={styles.tabCount}>{sessionCount}</span>}
                </TabsTrigger>
              )}
            </TabsList>
            <div className={styles.searchBar}>
              <Search size={12} className={styles.searchIcon} />
              <input
                id="persona-search"
                name="persona-search"
                type="text"
                placeholder="Search personas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
                autoFocus
              />
              <button
                onClick={() => { setShowAiCreate(!showAiCreate); setTimeout(() => aiInputRef.current?.focus(), 50); }}
                title="Create persona with AI"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)',
                  background: showAiCreate ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'transparent',
                  color: showAiCreate ? 'var(--primary)' : 'var(--muted-foreground)',
                  cursor: 'pointer', fontSize: '0.72rem', fontWeight: 500, whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease', flexShrink: 0,
                }}
              >
                <Sparkles size={11} /> Create
              </button>
            </div>
            {showAiCreate && (
              <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <textarea
                  ref={aiInputRef}
                  placeholder="Describe the persona you want... e.g. 'A DevOps engineer who specializes in Kubernetes and CI/CD pipelines'"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiGenerate(); } }}
                  rows={2}
                  style={{
                    width: '100%', resize: 'none', padding: '6px 8px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--background)', color: 'var(--foreground)',
                    fontSize: '0.8rem', outline: 'none', fontFamily: 'inherit',
                  }}
                  disabled={aiGenerating}
                />
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setShowAiCreate(false); setAiPrompt(''); }}
                    style={{ padding: '4px 10px', borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted-foreground)', cursor: 'pointer', fontSize: '0.75rem' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAiGenerate}
                    disabled={aiGenerating || !aiPrompt.trim()}
                    style={{
                      padding: '4px 12px', borderRadius: 5, border: 'none',
                      background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontSize: '0.75rem',
                      opacity: aiGenerating || !aiPrompt.trim() ? 0.5 : 1,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    {aiGenerating ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className={styles.loading}>Loading personas...</div>
          ) : allPersonas.length === 0 ? (
            <div className={styles.empty}>No personas available</div>
          ) : filteredPersonas.length === 0 ? (
            <div className={styles.noResults}>No personas match "{searchQuery}"</div>
          ) : (
            <>
              <TabsContent value="my" className={styles.tabPanel}>
                <div className={styles.list}>
                  {sortedPersonas.map((persona) => {
                    const IconComponent = getIconComponent(persona.icon);
                    return (
                      <button
                        key={persona.id}
                        className={`${styles.item} ${persona.isEnabled ? styles.itemEnabled : ''}`}
                        onClick={() => handleTogglePersona(persona.id, persona.isEnabled)}
                      >
                        <span className={styles.itemIcon}>
                          <IconComponent size={16} />
                        </span>
                        <div className={styles.itemInfo}>
                          <span className={styles.itemName}>{persona.name}</span>
                          <span className={styles.itemCategory}>{persona.category}</span>
                        </div>
                        {persona.isEnabled && <Check size={14} className={styles.checkIcon} />}
                      </button>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="session" className={styles.tabPanel}>
                <div className={styles.list}>
                  {/* Active participants section */}
                  {sessionCount > 0 && (
                    <div className={styles.participantsSection}>
                      <div className={styles.sectionLabel}>Active Participants</div>
                      {participants.map((participant) => {
                        const PersonaIcon = getIconComponent(participant.personaIcon);
                        return (
                          <div key={participant.id} className={styles.participantCard}>
                            <span className={styles.participantAvatar}>
                              {participant.avatarUrl ? (
                                <img
                                  src={participant.avatarUrl}
                                  alt=""
                                  className={styles.avatarImg}
                                />
                              ) : (
                                <PersonaIcon size={14} />
                              )}
                            </span>
                            <div className={styles.participantInfo}>
                              <span className={styles.participantName}>
                                {(participant.displayName || participant.personaName).split(' ')[0]}
                              </span>
                              <span className={styles.participantRole}>
                                {participant.personaName}
                              </span>
                            </div>
                            <button
                              className={styles.removeBtn}
                              onClick={() =>
                                handleRemoveParticipant(
                                  participant.id,
                                  participant.displayName,
                                  participant.personaName,
                                )
                              }
                              disabled={removeParticipant.isPending}
                              title="Remove participant"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Divider */}
                  {sessionCount > 0 && <div className={styles.sectionDivider} />}

                  {/* Persona catalog - add new participants */}
                  <div className={styles.participantsSection}>
                    <div className={styles.sectionLabel}>Add Participant</div>
                    {sortedForSession.map((persona) => {
                      const PersonaIcon = getIconComponent(persona.icon);
                      const activeCount = participantsByPersona.get(persona.id)?.length || 0;
                      return (
                        <div
                          key={persona.id}
                          className={`${styles.item} ${activeCount > 0 ? styles.itemActive : ''}`}
                        >
                          <span className={styles.itemIcon}>
                            <PersonaIcon size={16} />
                          </span>
                          <div className={styles.itemInfo}>
                            <span className={styles.itemName}>
                              {persona.name}
                              {activeCount > 0 && (
                                <span className={styles.activeIndicator}>
                                  {' '}
                                  ({activeCount} active)
                                </span>
                              )}
                            </span>
                            <span className={styles.itemCategory}>{persona.category}</span>
                          </div>
                          <button
                            className={styles.addBtn}
                            onClick={() => handleAddParticipant(persona.id, persona.name)}
                            disabled={addingPersonaId === persona.id}
                            title={`Add a new ${persona.name}`}
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};
